import { defineStore } from 'pinia'
import { ObjectKindTypeId, type ObjectRoomId } from '@previo/codebooks'
import { $t, useLazyStore } from '@previo/core'
import dayjs from 'dayjs'
import { DateTimeRange } from '@previo/design-system'
import { useAPI } from '~/http'
import type { PropertyObjectKind, PropertyObjectRoom } from '~/http/property/types'
import { ObjectKind } from '~/store/property/objectKinds/model/ObjectKind'
import { ObjectRoom } from '~/store/property/objectKinds/model/ObjectRoom'

export const usePropertyObjectKindsStore = defineStore('propertyObjectKinds', () => {
  const loadObjectKinds = async () => {
    const r = await useAPI().propertyAPI.objectKinds()

    return r.map((responseObjectKind: PropertyObjectKind) => {
      const objectKind = new ObjectKind(
        responseObjectKind.objectKindId,
        responseObjectKind.name,
        responseObjectKind.objectKindTypeId,
        responseObjectKind.numberOfBeds,
        responseObjectKind.numberOfExtraBeds,
        responseObjectKind.maxWithoutBeds,
        responseObjectKind.slotInterval,
        responseObjectKind.openingHours.map((intervals) =>
          intervals.map((interval) => new DateTimeRange(dayjs(interval.from), dayjs(interval.to))),
        ),
      )

      const objectRooms = responseObjectKind.objectRooms.map((objectRoom: PropertyObjectRoom) => {
        return new ObjectRoom(objectRoom.objectRoomId, objectRoom.name, objectKind)
      })

      objectKind.setObjectRooms(objectRooms)

      return objectKind
    })
  }

  const objectKinds = useLazyStore(loadObjectKinds)

  const getObjectKindById = (objectKindId: number): ObjectKind | null => {
    return (
      objectKinds.list.value.find(
        (objectKind: ObjectKind) => objectKind.objectKindId === objectKindId,
      ) || null
    )
  }

  const getObjectKindByObjectRoomId = (objectRoomId: ObjectRoomId): ObjectKind | null => {
    return getObjectRoomById(objectRoomId)?.objectKind || null
  }

  const getObjectRoomById = (objectRoomId: ObjectRoomId): ObjectRoom | null => {
    return (
      getAllObjectRooms().find(
        (objectRoom: ObjectRoom) => objectRoom.objectRoomId === objectRoomId,
      ) || null
    )
  }

  const getObjectRoomsByIds = (objectRoomIds: ObjectRoomId[]): ObjectRoom[] => {
    return getAllObjectRooms().filter((objectRoom: ObjectRoom) =>
      objectRoomIds.includes(objectRoom.objectRoomId),
    )
  }

  const getObjectRoomName = (objectRoomId: ObjectRoomId): string => {
    return getObjectRoomById(objectRoomId)?.name ?? $t('Room {id}', { id: objectRoomId })
  }

  const getAllObjectRooms = (): ObjectRoom[] => {
    return objectKinds.list.value.flatMap((objectKind: ObjectKind) => objectKind.objectRooms)
  }

  const getAllObjectKinds = (): ObjectKind[] => {
    return objectKinds.list.value
  }

  const getObjectKindsByType = (objectKindTypeId: ObjectKindTypeId): ObjectKind[] => {
    return objectKinds.list.value.filter(
      (objectKind: ObjectKind) => objectKind.objectKindTypeId === objectKindTypeId,
    )
  }

  const getObjectKindsByTypes = (objectKindTypeIds: ObjectKindTypeId[]): ObjectKind[] => {
    return objectKinds.list.value.filter((objectKind: ObjectKind) =>
      objectKindTypeIds.includes(objectKind.objectKindTypeId),
    )
  }

  const getObjectRoomsByType = (objectKindTypeId: ObjectKindTypeId): ObjectRoom[] => {
    return getObjectKindsByType(objectKindTypeId).flatMap(
      (objectKind: ObjectKind) => objectKind.objectRooms,
    )
  }

  const getAllHourlyObjectKinds = (): ObjectKind[] => {
    return objectKinds.list.value.filter((objectKind: ObjectKind) => objectKind.isHourlyBased())
  }

  const getAllVenueObjectKinds = (): ObjectKind[] => {
    return objectKinds.list.value.filter(
      (objectKind: ObjectKind) => objectKind.objectKindTypeId !== ObjectKindTypeId.ACCOMMODATION,
    )
  }

  const getObjectRoomsByTypeWithSameOrBiggerCapacity = (
    objectKindTypeId: ObjectKindTypeId,
    objectRoomId: ObjectRoomId,
  ): ObjectRoom[] => {
    const objectRoomToCompare = getObjectKindByObjectRoomId(objectRoomId)

    if (!objectRoomToCompare) {
      return []
    }

    return getObjectRoomsByType(objectKindTypeId).filter((objectRoom: ObjectRoom) => {
      const objectKind = getObjectKindByObjectRoomId(objectRoom.objectRoomId)

      if (!objectKind) {
        return false
      }

      return objectKind.totalCapacity() >= objectRoomToCompare.totalCapacity()
    })
  }

  const hasAnyRooms = (): boolean => {
    return getAllObjectRooms().length > 0
  }

  return {
    ...objectKinds,
    load: objectKinds.load,
    getAllObjectKinds,
    getAllObjectRooms,
    getObjectRoomsByType,
    getObjectKindsByType,
    getObjectKindsByTypes,
    getObjectKindById,
    getObjectRoomById,
    getObjectRoomName,
    getObjectKindByObjectRoomId,
    getAllHourlyObjectKinds,
    getAllVenueObjectKinds,
    getObjectRoomsByIds,
    getObjectRoomsByTypeWithSameOrBiggerCapacity,
    hasAnyRooms,
  }
})
